/* Edits an older build left on this device.

   Before collections could be written to, an edited or newly written chart was kept whole in
   IndexedDB, under `edits:<collection id>`, and laid over the collection when it was read.
   Charts are saved into a collection now, so nothing is laid over anything any more: what is
   still here is read for one purpose, to be moved into a collection (or thrown away) from the
   collections page. Nothing in this file deletes an edit on its own. */

import { browserStore, type KV } from "@preflop/api";
import { spotFromRow, spotRowSchema, type Spot } from "@preflop/core";

const PREFIX = "edits:";

export interface DeviceEdits {
  /** The collection the charts were edits of, or additions to. */
  collectionId: string;
  spots: Spot[];
}

/** Every collection this device holds edits over, with the charts that still read. */
export async function deviceEdits(store: KV = browserStore("edits")): Promise<DeviceEdits[]> {
  const out: DeviceEdits[] = [];
  for (const slot of await store.keys()) {
    if (!slot.startsWith(PREFIX)) continue;
    const spots: Spot[] = [];
    for (const raw of (await store.get<unknown[]>(slot)) ?? []) {
      // A chart written by a build this one can no longer read is skipped, not moved.
      const row = spotRowSchema.safeParse(raw);
      if (row.success) spots.push(spotFromRow(row.data));
    }
    if (spots.length) out.push({ collectionId: slot.slice(PREFIX.length), spots });
  }
  return out;
}

/** How many charts are waiting on this device, over every collection. */
export const countDeviceEdits = async (store?: KV): Promise<number> =>
  (await deviceEdits(store)).reduce((n, e) => n + e.spots.length, 0);

/** Forget the edits made over one collection: after they were moved, or on request. */
export const discardDeviceEdits = (
  collectionId: string,
  store: KV = browserStore("edits"),
): Promise<void> => store.delete(PREFIX + collectionId);
